import React, { PropsWithChildren } from 'react';
import type { Mermaid, ParseResult, RenderResult } from 'mermaid';
import { generateRandomColor } from 'Helpers/utils';
import { useTheme } from '@mui/material';
import tokensDark from '../../../../packages/pounceV2/src/theme/tokens-dark';

export interface MermaidContextValue {
  getMermaidHandle: () => Promise<Mermaid>;
  renderSvg: (mermaidBlock: string) => Promise<RenderResult>;
  parse: (mermaidBlock: string) => Promise<MermaidParsed>;
  cachedSvgs: { [key: string]: RenderResult };
}

export interface MermaidParsed {
  /**
   * whether or not the parsing succeeded. if true, parseResult will be set
   */
  ok: boolean;

  /**
   * null if parsing failed
   */
  parseResult?: ParseResult;

  /**
   * the original mermaidBlock string that was provided to the parse function
   */
  originalText: string;
}

type MermaidTokens = typeof tokensDark;

type MermaidColors = {
  neutralCanvasBase: string;
  text: string;
  teal800: string;
  teal500: string;
  blue700: string;
  blue600: string;
  blue800: string;
  navy800: string;
  navy400: string;
  gray300: string;
  gray700: string;
  yellow200: string;
  yellow600: string;
  cyan500: string;
  cyan800: string;
  cyan600: string;
  indigo500: string;
  indigo800: string;
  violet300: string;
  purple400: string;
  magenta400: string;
  pink500: string;
  orange500: string;
  green500: string;
};

function getMermaidColors(tokens: MermaidTokens): MermaidColors {
  return {
    neutralCanvasBase: tokens.background.basic.clear,
    text: tokens.content.text.neutral.base,
    teal800: tokens.content.assets.accent[3][1],
    teal500: tokens.content.assets.accent[3][3],
    blue700: tokens.content.assets.brand[2],
    blue600: tokens.content.assets.accent[1][2],
    blue800: tokens.background.brand.blue[2],
    navy800: tokens.background.secondary[1],
    navy400: tokens.content.assets.secondary[4],
    gray300: tokens.content.assets.neutral[4],
    gray700: tokens.content.text.neutral[2],
    yellow200: tokens.content.assets.semantic.warning[4],
    yellow600: tokens.content.assets.semantic.warning[2],
    cyan500: tokens.content.assets.accent[3][3],
    cyan800: tokens.content.assets.accent[3][1],
    cyan600: tokens.stroke.accent[3][3],
    indigo500: tokens.content.assets.accent[8][3],
    indigo800: tokens.content.assets.accent[8][1],
    violet300: tokens.content.assets.accent[2][4],
    purple400: tokens.content.assets.accent[8][4],
    magenta400: tokens.content.assets.accent[9][3],
    pink500: tokens.content.assets.accent[9][2],
    orange500: tokens.content.assets.accent[4][3],
    green500: tokens.content.assets.semantic.success[2],
  };
}

function buildFlowchartCss(c: MermaidColors) {
  return `
.node rect, .node path, .node circle, .node polygon {
  stroke-width: 3px !important;
  rx: 12px !important;
  ry: 12px !important;
  fill: ${c.neutralCanvasBase} !important;
  stroke: ${c.blue700} !important;
}

svg text, svg p {
  font-family: 'DM sans' !important;
}

.nodeLabel {
  color: ${c.text} !important;
}
.marker {
  color: ${c.teal500} !important;
  fill: ${c.teal500} !important;
}
.flowchart-link {
  stroke: ${c.teal500} !important;
  color: ${c.teal500} !important;
}
.edge-thickness-normal {
  stroke-width: 2px !important;
}

g.label foreignObject {
  overflow: visible !important;
}

.edgeLabel p {
  border: 1.5px solid ${c.teal500} !important;
  border-radius: 500px !important;
  padding-left: 8px !important;
  padding-right: 8px !important;
  padding-top: 3px !important;
  padding-bottom: 3px !important;
  background-color: unset !important;
  font-weight: 400 !important;
  font-size: 14px !important;
  color: ${c.text} !important;
  background-color: ${c.teal800} !important;
}

.labelBkg {
  background-color: unset !important;
}

.cluster > rect {
  rx: 10px !important;
  ry: 10px !important;
  fill: none !important;
  stroke: ${c.yellow600} !important;
  stroke-width: 2px !important;
}

.cluster-label > foreignObject {
  transform: translate(0px, 4px) !important;
}
`;
}

type SectionColor = 'Blue' | 'Cyan' | 'Indigo' | 'Violet' | 'Magenta' | 'Pink' | 'Orange';

type TimelineNodeColors = {
  headingBorder: string;
  headingFill: string;
  eventBorder: string;
  eventFill: string;
};

function getTimelineSectionNodeColors(
  sectionColor: SectionColor,
  c: MermaidColors,
): TimelineNodeColors {
  switch (sectionColor) {
    case 'Blue':
      return {
        headingBorder: c.blue700,
        headingFill: c.neutralCanvasBase,
        eventBorder: c.blue600,
        eventFill: c.blue800,
      };
    case 'Cyan':
      return {
        headingBorder: c.cyan600,
        headingFill: c.cyan800,
        eventBorder: c.cyan500,
        eventFill: c.cyan800,
      };
    case 'Indigo':
      return {
        headingBorder: c.indigo800,
        headingFill: c.indigo800,
        eventBorder: c.indigo500,
        eventFill: c.indigo800,
      };
    case 'Violet':
      return {
        headingBorder: c.violet300,
        headingFill: c.indigo800,
        eventBorder: c.violet300,
        eventFill: c.indigo800,
      };
    case 'Magenta':
      return {
        headingBorder: c.magenta400,
        headingFill: c.indigo800,
        eventBorder: c.magenta400,
        eventFill: c.indigo800,
      };
    case 'Pink':
      return {
        headingBorder: c.pink500,
        headingFill: c.indigo800,
        eventBorder: c.pink500,
        eventFill: c.indigo800,
      };
    case 'Orange':
      return {
        headingBorder: c.orange500,
        headingFill: c.indigo800,
        eventBorder: c.orange500,
        eventFill: c.indigo800,
      };
    default:
      return {
        headingBorder: c.orange500,
        headingFill: c.indigo800,
        eventBorder: c.orange500,
        eventFill: c.indigo800,
      };
  }
}

function getTimelineSectionNodeCss(
  index: number,
  sectionColor: SectionColor,
  c: MermaidColors,
): string {
  const colors = getTimelineSectionNodeColors(sectionColor, c);
  return `
g.timeline-node.section-${index} {
  outline: 3px solid ${colors.headingBorder} !important;
  border-radius: 3px !important;
}

g.timeline-node.section-${index} path {
  fill: ${colors.headingFill} !important;
}

.eventWrapper g.timeline-node.section-${index} {
  outline: 3px solid ${colors.eventBorder} !important;
  border-radius: 3px !important;
}

.eventWrapper g.timeline-node.section-${index} path {
  fill: ${colors.eventFill} !important;
}

g.timeline-node text {
  fill: ${c.text} !important;
  font-size: 14px !important;
  font-family: 'DM sans' !important;
  font-weight: 400 !important;
}
  `;
}

function buildTimelineCss(c: MermaidColors): string {
  const sections: Array<[number, SectionColor]> = [
    [-1, 'Blue'],
    [0, 'Cyan'],
    [1, 'Indigo'],
    [2, 'Violet'],
    [3, 'Magenta'],
    [4, 'Pink'],
    [5, 'Orange'],
    [6, 'Blue'],
    [7, 'Cyan'],
    [8, 'Indigo'],
    [9, 'Violet'],
    [10, 'Magenta'],
    [11, 'Pink'],
    [12, 'Orange'],
    [13, 'Blue'],
    [14, 'Cyan'],
    [15, 'Indigo'],
    [16, 'Violet'],
    [17, 'Magenta'],
    [18, 'Pink'],
    [19, 'Orange'],
  ];
  return `
${sections.map(([i, color]) => getTimelineSectionNodeCss(i, color, c)).join('\n')}

.timeline-node line {
  display: none !important;
}

.lineWrapper line {
  stroke: ${c.teal500} !important;
}

#arrowhead {
  fill: ${c.teal500} !important;
}

svg text {
  font-weight: 700 !important;
  fill: ${c.text} !important;
  font-size: 14px !important;
  font-family: 'DM sans' !important;
}
`;
}

function buildXyCss(c: MermaidColors) {
  return `
.plot [class*="bar-plot"] rect {
  fill: ${c.blue600} !important;
  stroke: ${c.blue600} !important;
}

.chart-title text {
  font-weight: 700 !important;
  fill: ${c.text} !important;
  color: ${c.text} !important;
  font-size: 14px !important;
  font-family: 'DM sans' !important;
}

.main > rect {
  fill: ${c.neutralCanvasBase} !important;
}

.left-axis .label text {
  fill: ${c.text} !important;
  font-weight: 400 !important;
  font-size: 14px !important;
  font-family: 'DM sans' !important;
}

.bottom-axis .label text {
  fill: ${c.text} !important;
  font-weight: 400 !important;
  font-size: 14px !important;
  font-family: 'DM sans' !important;
  text-transform: capitalize !important;
}

.left-axis .title text {
  fill: ${c.text} !important;
  font-weight: 400 !important;
  font-size: 14px !important;
  font-family: 'DM sans' !important;
}

.bottom-axis .title text {
  fill: ${c.text} !important;
  font-weight: 400 !important;
  font-size: 14px !important;
  font-family: 'DM sans' !important;
}

[class*="line-plot"] > path {
  stroke: ${c.teal500} !important;
}

.axisl-line > path {
  stroke: ${c.navy400} !important;
  fill: ${c.navy400} !important;
}
.axis-line > path {
  stroke: ${c.navy400} !important;
  fill: ${c.navy400} !important;
}

.ticks > path {
  visibility: hidden !important;
}
`;
}

function buildSequenceCss(c: MermaidColors) {
  return `
  rect.actor {
    stroke-width: 3px !important;
    stroke: ${c.blue700} !important;
    fill: ${c.neutralCanvasBase} !important;
  }

  text, text > tspan {
    font-family: 'DM sans' !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    fill: ${c.text} !important;
    stroke: none !important;
  }

  text.messageText {
    outline: 1.5px solid ${c.teal500};
    border-radius: 500px;
    outline-offset: 4px;
  }

  line {
    stroke: ${c.teal500} !important;
    stroke-width: 2px !important;
  }

  marker#arrowhead > path {
    stroke: ${c.teal500} !important;
    fill: ${c.teal500} !important;
  }

  marker#arrowhead > path {
    stroke: ${c.teal500} !important;
    fill: ${c.teal500} !important;
  }

  marker#filled-head > path {
    stroke: none !important;
    fill: ${c.teal500} !important;
  }

  rect.note {
    fill: ${c.yellow200} !important;
    stroke: none !important;
  }

  text.noteText > tspan {
    fill: ${c.gray700} !important;
  }

  rect[class*="activation"] {
    fill: ${c.cyan800} !important;
    stroke: ${c.cyan600} !important;
    stroke-width: 3px !important;
    rx: 2px !important;
    ry: 2px !important;
  }

  .actor-man > line {
    stroke-width: 3px !important;
    stroke: ${c.blue600} !important;
  }

  .actor-man > circle {
    stroke-width: 3px !important;
    stroke: ${c.blue600} !important;
    fill: ${c.blue800} !important;
  }

  .loopLine {
    stroke: ${c.indigo500} !important;
  }

  .labelBox {
    stroke: ${c.indigo500} !important;
    fill: ${c.indigo800} !important;
    stroke-width: 1.5px !important;
  }
`;
}

function buildSankeyCss(c: MermaidColors) {
  return `
  linearGradient {
    display: none !important;
  }

  .link {
    mix-blend-mode: unset !important;
  }

  text {
    font-family: 'DM sans' !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    fill: ${c.text} !important;
    stroke: none !important;
  }
`;
}

type Replacement = {
  replace: string;
  with: string;
  once?: boolean;
};

export type MermaidStyling = {
  flowchartCss: string;
  timelineCss: string;
  xyCss: string;
  sequenceCss: string;
  sankeyCss: string;
  sankeyReplacements: Replacement[];
};

function buildMermaidStyling(tokens: MermaidTokens): MermaidStyling {
  const c = getMermaidColors(tokens);
  return {
    flowchartCss: buildFlowchartCss(c),
    timelineCss: buildTimelineCss(c),
    xyCss: buildXyCss(c),
    sequenceCss: buildSequenceCss(c),
    sankeyCss: buildSankeyCss(c),
    sankeyReplacements: [
      { replace: '#4e79a7', with: c.blue600 },
      { replace: '#f28e2c', with: c.cyan500 },
      { replace: '#af7aa1', with: c.indigo500 },
      { replace: '#ff9da7', with: c.violet300 },
      { replace: '#9c755f', with: c.purple400 },
      { replace: '#bab0ab', with: c.magenta400 },
      { replace: '#edc949', with: c.pink500 },
      { replace: '#76b7b2', with: c.orange500 },
      { replace: '#e15759', with: c.yellow600 },
      { replace: '#59a14f', with: c.green500 },
      {
        once: true,
        replace: 'class="node-labels"',
        with: 'id="node-labels" class="node-labels"',
      },
      { once: true, replace: '</svg>', with: '<use xlink:href="#node-labels" /></svg>' },
    ],
  };
}

const defaultStyling = buildMermaidStyling(tokensDark);

/**
 * adds a custom <style> tag into the rendered result SVG according to the diagram type.
 * if diagram type not supported and/or unknown, no action is performed and renderResult is returned as-is.
 * diagram types are listed here: https://github.com/mermaid-js/mermaid/blob/8dd0e7a794c72228739b45edaa73def0a1c86e8b/.build/jsonSchema.ts#L9
 */
export function applyCustomMermaidStyling(
  renderResult: RenderResult,
  styling: MermaidStyling = defaultStyling,
): RenderResult {
  let insertCss = '';
  let extraReplacements: Replacement[] = [];
  switch (renderResult.diagramType) {
    case 'flowchart-v2':
    case 'flowchart':
      insertCss = styling.flowchartCss;
      break;
    case 'timeline':
      insertCss = styling.timelineCss;
      break;
    case 'xychart':
      insertCss = styling.xyCss;
      break;
    case 'sequence':
      insertCss = styling.sequenceCss;
      break;
    case 'sankey':
      insertCss = styling.sankeyCss;
      extraReplacements = styling.sankeyReplacements;
      break;
    default:
      return renderResult;
  }

  const svg = renderResult.svg ?? '';
  let modifiedSvg = svg.replace('<g>', `<style>${insertCss}</style><g>`);
  // eslint-disable-next-line no-restricted-syntax
  for (const replacement of extraReplacements) {
    if (replacement.once) {
      modifiedSvg = modifiedSvg.replace(replacement.replace, replacement.with);
    } else {
      modifiedSvg = modifiedSvg.replaceAll(replacement.replace, replacement.with);
    }
  }
  return {
    bindFunctions: renderResult.bindFunctions,
    svg: modifiedSvg,
    diagramType: renderResult.diagramType,
  };
}

export const MermaidContextV3 = React.createContext<MermaidContextValue>(undefined);

export const MermaidProviderV3: React.FC<PropsWithChildren> = ({ children }) => {
  const theme = useTheme();
  const tokens = (theme.palette as { tokens?: MermaidTokens }).tokens ?? tokensDark;
  const styling = React.useMemo(() => buildMermaidStyling(tokens), [tokens]);

  const [mermaidHandle, setMermaidHandle] = React.useState<Mermaid>(null);
  const cachedSvgs = React.useRef<{ [key: string]: RenderResult }>({});

  React.useEffect(() => {
    cachedSvgs.current = {};
    setMermaidHandle(null);
  }, [tokens]);

  const primaryTextColor = getMermaidColors(tokens).text;

  const getMermaidHandleLazyLoad = React.useCallback(async () => {
    if (!mermaidHandle) {
      const mermaid = await import('mermaid');
      mermaid.default.initialize({
        startOnLoad: false,
        themeVariables: {
          fontSize: '14px',
          primaryTextColor,
        },
        xyChart: {
          yAxis: {
            titlePadding: 10,
          },
          xAxis: {
            labelPadding: 12,
          },
        },
        sankey: {
          linkColor: 'source',
          showValues: false,
        },
      });
      setMermaidHandle(mermaid.default);
      return mermaid.default;
    }
    return mermaidHandle;
  }, [mermaidHandle, primaryTextColor]);

  const renderSvg = React.useCallback(
    async (mermaidBlock: string) => {
      try {
        const hash = generateRandomColor();
        const handle = await getMermaidHandleLazyLoad();
        const result = await handle.render(`mermaid-id-${hash}`, mermaidBlock);
        const resultWithAppliedStyles = applyCustomMermaidStyling(result, styling);
        cachedSvgs.current[mermaidBlock] = resultWithAppliedStyles;
        return resultWithAppliedStyles;
      } catch (error) {
        return {
          svg: null,
        } as RenderResult;
      }
    },
    [getMermaidHandleLazyLoad, styling],
  );

  const parse = React.useCallback<(mermaidBlock: string) => Promise<MermaidParsed>>(
    async (mermaidBlock: string) => {
      const handle = await getMermaidHandleLazyLoad();
      // we dont want it to throw
      const out = await handle.parse(mermaidBlock, { suppressErrors: true });
      const outResult = {
        ok: true,
        parseResult: null,
        originalText: mermaidBlock,
      };
      if (typeof out === 'boolean') {
        outResult.ok = false;
      } else {
        outResult.parseResult = out;
      }
      return outResult;
    },
    [getMermaidHandleLazyLoad],
  );

  return (
    <MermaidContextV3.Provider
      value={{
        getMermaidHandle: getMermaidHandleLazyLoad,
        renderSvg,
        parse,
        cachedSvgs: cachedSvgs.current,
      }}
    >
      {children}
    </MermaidContextV3.Provider>
  );
};
